<template>
  <div class="q-pa-md">
    <div class="row justify-center">
      <div class="col-12 col-lg-10">
        <!-- Заголовок -->
        <q-card class="q-mb-lg">
          <q-card-section class="text-center bg-primary text-white">
            <div class="text-h4 q-mb-sm">
              <q-icon name="facebook" class="q-mr-md" />
              {{ t('about.title') }}
            </div>
            <div class="text-subtitle1">
              {{ t('about.subtitle') }}
            </div>
          </q-card-section>
        </q-card>

        <!-- Оглавление -->
        <q-card class="q-mb-lg">
          <q-card-section>
            <div class="text-h6 q-mb-md">📋 {{ t('about.toc') }}</div>
            <div class="row">
              <div class="col-12 col-md-6">
                <q-list dense>
                  <q-item clickable @click="scrollTo('getting-started')">
                    <q-item-section>1. {{ t('about.sections.getting_started') }}</q-item-section>
                  </q-item>
                  <q-item clickable @click="scrollTo('facebook-setup')">
                    <q-item-section>2. {{ t('about.sections.facebook_setup') }}</q-item-section>
                  </q-item>
                  <q-item clickable @click="scrollTo('account-management')">
                    <q-item-section>3. {{ t('about.sections.account_management') }}</q-item-section>
                  </q-item>
                  <q-item clickable @click="scrollTo('sending-conversions')">
                    <q-item-section>4. {{ t('about.sections.sending_conversions') }}</q-item-section>
                  </q-item>
                </q-list>
              </div>
              <div class="col-12 col-md-6">
                <q-list dense>
                  <q-item clickable @click="scrollTo('external-api')">
                    <q-item-section>5. {{ t('about.sections.external_api') }}</q-item-section>
                  </q-item>
                  <q-item clickable @click="scrollTo('api-examples')">
                    <q-item-section>6. {{ t('about.sections.api_examples') }}</q-item-section>
                  </q-item>
                  <q-item clickable @click="scrollTo('troubleshooting')">
                    <q-item-section>7. {{ t('about.sections.troubleshooting') }}</q-item-section>
                  </q-item>
                  <q-item clickable @click="scrollTo('best-practices')">
                    <q-item-section>8. {{ t('about.sections.best_practices') }}</q-item-section>
                  </q-item>
                </q-list>
              </div>
            </div>
          </q-card-section>
        </q-card>

        <!-- 1. Начало работы -->
        <q-card id="getting-started" class="q-mb-lg">
          <q-card-section>
            <div class="text-h5 q-mb-md">🚀 1. {{ t('about.sections.getting_started') }}</div>

            <div class="text-h6 q-mt-lg q-mb-sm">{{ t('about.getting_started.what_is') }}</div>
            <p class="text-body1">
              {{ t('about.getting_started.description') }}
            </p>

            <div class="text-h6 q-mt-lg q-mb-sm">{{ t('about.getting_started.features') }}</div>
            <q-list bordered separator class="q-mt-sm">
              <q-item>
                <q-item-section avatar><q-icon color="primary" name="link" /></q-item-section>
                <q-item-section>
                  <q-item-label>{{ t('about.getting_started.connect_accounts') }}</q-item-label>
                  <q-item-label caption>{{ t('about.getting_started.connect_accounts_desc') }}</q-item-label>
                </q-item-section>
              </q-item>
              <q-item>
                <q-item-section avatar><q-icon color="primary" name="track_changes" /></q-item-section>
                <q-item-section>
                  <q-item-label>{{ t('about.getting_started.manage_pixels') }}</q-item-label>
                  <q-item-label caption>{{ t('about.getting_started.manage_pixels_desc') }}</q-item-label>
                </q-item-section>
              </q-item>
              <q-item>
                <q-item-section avatar><q-icon color="primary" name="send" /></q-item-section>
                <q-item-section>
                  <q-item-label>{{ t('about.getting_started.send_conversions') }}</q-item-label>
                  <q-item-label caption>{{ t('about.getting_started.send_conversions_desc') }}</q-item-label>
                </q-item-section>
              </q-item>
              <q-item>
                <q-item-section avatar><q-icon color="primary" name="analytics" /></q-item-section>
                <q-item-section>
                  <q-item-label>{{ t('about.getting_started.analytics') }}</q-item-label>
                  <q-item-label caption>{{ t('about.getting_started.analytics_desc') }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>

            <div class="text-h6 q-mt-lg q-mb-sm">{{ t('about.getting_started.who_can_use') }}</div>
            <q-banner rounded class="bg-blue-1 q-mt-sm">
              <template v-slot:avatar>
                <q-icon name="info" color="primary" />
              </template>
              <strong>{{ t('users.role.user') }}:</strong> {{ t('about.getting_started.user_role') }}<br>
              <strong>{{ t('users.role.admin') }}:</strong> {{ t('about.getting_started.admin_role') }}
            </q-banner>
          </q-card-section>
        </q-card>

        <!-- 2. Настройка Facebook -->
        <q-card id="facebook-setup" class="q-mb-lg">
          <q-card-section>
            <div class="text-h5 q-mb-md">⚙️ 2. {{ t('about.sections.facebook_setup') }}</div>

            <div class="text-h6 q-mb-sm">{{ t('about.facebook_setup.requirements') }}</div>
            <q-list bordered separator>
              <q-item>
                <q-item-section avatar><q-icon color="positive" name="check_circle" /></q-item-section>
                <q-item-section>
                  <q-item-label>{{ t('about.facebook_setup.business_manager') }}</q-item-label>
                  <q-item-label caption>{{ t('about.facebook_setup.business_manager_desc') }}</q-item-label>
                </q-item-section>
              </q-item>
              <q-item>
                <q-item-section avatar><q-icon color="positive" name="check_circle" /></q-item-section>
                <q-item-section>
                  <q-item-label>{{ t('about.facebook_setup.admin_rights') }}</q-item-label>
                  <q-item-label caption>{{ t('about.facebook_setup.admin_rights_desc') }}</q-item-label>
                </q-item-section>
              </q-item>
              <q-item>
                <q-item-section avatar><q-icon color="positive" name="check_circle" /></q-item-section>
                <q-item-section>
                  <q-item-label>{{ t('about.facebook_setup.domain_verification') }}</q-item-label>
                  <q-item-label caption>{{ t('about.facebook_setup.domain_verification_desc') }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>

            <div class="text-h6 q-mt-lg q-mb-sm">{{ t('about.facebook_setup.permissions') }}</div>
            <q-banner rounded class="bg-orange-1 q-mt-sm">
              <template v-slot:avatar>
                <q-icon name="warning" color="orange" />
              </template>
              {{ t('about.facebook_setup.permissions_warning') }}
              <ul class="q-ma-sm">
                <li><strong>{{ t('about.facebook_setup.ads_management') }}</strong></li>
                <li><strong>{{ t('about.facebook_setup.business_management') }}</strong></li>
                <li><strong>{{ t('about.facebook_setup.read_insights') }}</strong></li>
              </ul>
            </q-banner>
          </q-card-section>
        </q-card>

        <!-- 3. Управление аккаунтами -->
        <q-card id="account-management" class="q-mb-lg">
          <q-card-section>
            <div class="text-h5 q-mb-md">👥 3. {{ t('about.sections.account_management') }}</div>

            <div class="text-h6 q-mb-sm">{{ t('about.account_management.connecting_account') }}</div>
            <q-stepper v-model="step" vertical color="primary" animated>
              <q-step :name="1" :title="t('about.account_management.step_add')" icon="add" :done="step > 1">
                <div>{{ t('about.account_management.step_add_desc') }}</div>
              </q-step>
              <q-step :name="2" :title="t('about.account_management.step_auth')" icon="facebook" :done="step > 2">
                <div>{{ t('about.account_management.step_auth_desc') }}</div>
              </q-step>
              <q-step :name="3" :title="t('about.account_management.step_resources')" icon="business" :done="step > 3">
                <div>{{ t('about.account_management.step_resources_desc') }}</div>
              </q-step>
              <q-step :name="4" :title="t('about.account_management.step_sync')" icon="sync" :done="step > 4">
                <div>{{ t('about.account_management.step_sync_desc') }}</div>
              </q-step>
            </q-stepper>

            <div class="text-h6 q-mt-lg q-mb-sm">{{ t('about.account_management.managing_pixels') }}</div>
            <q-list bordered separator>
              <q-item>
                <q-item-section avatar><q-icon color="primary" name="sync" /></q-item-section>
                <q-item-section>
                  <q-item-label>{{ t('about.account_management.auto_sync') }}</q-item-label>
                  <q-item-label caption>{{ t('about.account_management.auto_sync_desc') }}</q-item-label>
                </q-item-section>
              </q-item>
              <q-item>
                <q-item-section avatar><q-icon color="primary" name="add" /></q-item-section>
                <q-item-section>
                  <q-item-label>{{ t('about.account_management.manual_add') }}</q-item-label>
                  <q-item-label caption>{{ t('about.account_management.manual_add_desc') }}</q-item-label>
                </q-item-section>
              </q-item>
              <q-item>
                <q-item-section avatar><q-icon color="primary" name="visibility" /></q-item-section>
                <q-item-section>
                  <q-item-label>{{ t('about.account_management.view_pixels') }}</q-item-label>
                  <q-item-label caption>{{ t('about.account_management.view_pixels_desc') }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-card-section>
        </q-card>

        <!-- 4. Отправка конверсий -->
        <q-card id="sending-conversions" class="q-mb-lg">
          <q-card-section>
            <div class="text-h5 q-mb-md">📤 4. {{ t('about.sections.sending_conversions') }}</div>

            <div class="text-h6 q-mb-sm">{{ t('about.sending_conversions.event_types') }}</div>
            <div class="row q-gutter-md q-mb-lg">
              <div class="col-12 col-md-6">
                <q-card flat bordered>
                  <q-card-section>
                    <div class="text-subtitle1 text-weight-bold q-mb-sm">📞 {{ t('about.sending_conversions.lead_title') }}</div>
                    <div class="text-caption">{{ t('about.sending_conversions.lead_desc') }}</div>
                    <q-list dense class="q-mt-sm">
                      <q-item v-for="example in leadExamples" :key="example" dense>
                        <q-item-section avatar><q-icon size="xs" name="fiber_manual_record" /></q-item-section>
                        <q-item-section>{{ example }}</q-item-section>
                      </q-item>
                    </q-list>
                  </q-card-section>
                </q-card>
              </div>
              <div class="col-12 col-md-6">
                <q-card flat bordered>
                  <q-card-section>
                    <div class="text-subtitle1 text-weight-bold q-mb-sm">💰 {{ t('about.sending_conversions.purchase_title') }}</div>
                    <div class="text-caption">{{ t('about.sending_conversions.purchase_desc') }}</div>
                    <q-list dense class="q-mt-sm">
                      <q-item v-for="example in purchaseExamples" :key="example" dense>
                        <q-item-section avatar><q-icon size="xs" name="fiber_manual_record" /></q-item-section>
                        <q-item-section>{{ example }}</q-item-section>
                      </q-item>
                    </q-list>
                  </q-card-section>
                </q-card>
              </div>
            </div>

            <div class="text-h6 q-mb-sm">{{ t('about.sending_conversions.data_quality') }}</div>
            <q-banner rounded class="bg-green-1 q-mb-md">
              <template v-slot:avatar>
                <q-icon name="grade" color="positive" />
              </template>
              <strong>{{ t('about.sending_conversions.quality_info') }}</strong>
            </q-banner>

            <div class="row q-gutter-md">
              <div class="col-12 col-md-4">
                <q-card flat bordered class="bg-red-1">
                  <q-card-section class="text-center">
                    <div class="text-h6 text-negative">1-3 {{ t('facebook.conversions.table.quality') }}</div>
                    <div class="text-caption">{{ t('about.sending_conversions.low_quality') }}</div>
                    <div class="q-mt-sm text-body2">{{ t('about.sending_conversions.low_quality_desc') }}</div>
                  </q-card-section>
                </q-card>
              </div>
              <div class="col-12 col-md-4">
                <q-card flat bordered class="bg-orange-1">
                  <q-card-section class="text-center">
                    <div class="text-h6 text-warning">4-7 {{ t('facebook.conversions.table.quality') }}</div>
                    <div class="text-caption">{{ t('about.sending_conversions.medium_quality') }}</div>
                    <div class="q-mt-sm text-body2">{{ t('about.sending_conversions.medium_quality_desc') }}</div>
                  </q-card-section>
                </q-card>
              </div>
              <div class="col-12 col-md-4">
                <q-card flat bordered class="bg-green-1">
                  <q-card-section class="text-center">
                    <div class="text-h6 text-positive">8-10 {{ t('facebook.conversions.table.quality') }}</div>
                    <div class="text-caption">{{ t('about.sending_conversions.high_quality') }}</div>
                    <div class="q-mt-sm text-body2">{{ t('about.sending_conversions.high_quality_desc') }}</div>
                  </q-card-section>
                </q-card>
              </div>
            </div>

            <div class="text-h6 q-mt-lg q-mb-sm">{{ t('about.sending_conversions.recommended_fields') }}</div>
            <div class="row q-gutter-md">
              <div class="col-12 col-md-6">
                <q-list bordered separator dense>
                  <q-item-label header>{{ t('about.sending_conversions.required_fields') }}</q-item-label>
                  <q-item>
                    <q-item-section avatar><q-icon color="red" name="star" /></q-item-section>
                    <q-item-section>{{ t('about.sending_conversions.email_or_phone') }}</q-item-section>
                  </q-item>
                  <q-item>
                    <q-item-section avatar><q-icon color="red" name="star" /></q-item-section>
                    <q-item-section>{{ t('about.sending_conversions.event_type') }}</q-item-section>
                  </q-item>
                </q-list>
              </div>
              <div class="col-12 col-md-6">
                <q-list bordered separator dense>
                  <q-item-label header>{{ t('about.sending_conversions.quality_fields') }}</q-item-label>
                  <q-item>
                    <q-item-section avatar><q-icon color="orange" name="recommended" /></q-item-section>
                    <q-item-section>{{ t('about.sending_conversions.full_name') }}</q-item-section>
                  </q-item>
                  <q-item>
                    <q-item-section avatar><q-icon color="orange" name="recommended" /></q-item-section>
                    <q-item-section>{{ t('about.sending_conversions.city_region') }}</q-item-section>
                  </q-item>
                  <q-item>
                    <q-item-section avatar><q-icon color="orange" name="recommended" /></q-item-section>
                    <q-item-section>{{ t('about.sending_conversions.ip_user_agent') }}</q-item-section>
                  </q-item>
                  <q-item>
                    <q-item-section avatar><q-icon color="orange" name="recommended" /></q-item-section>
                    <q-item-section>{{ t('about.sending_conversions.facebook_ids') }}</q-item-section>
                  </q-item>
                </q-list>
              </div>
            </div>
          </q-card-section>
        </q-card>

        <!-- 5. Внешний API -->
        <q-card id="external-api" class="q-mb-lg">
          <q-card-section>
            <div class="text-h5 q-mb-md">🔗 5. {{ t('about.external_api.title') }}</div>

            <div class="text-h6 q-mb-sm">{{ t('about.external_api.endpoints') }}</div>
            <q-list bordered separator>
              <q-item>
                <q-item-section avatar><q-icon color="primary" name="link" /></q-item-section>
                <q-item-section>
                  <q-item-label class="text-code">/convert</q-item-label>
                  <q-item-label caption>{{ t('about.external_api.short_url') }}</q-item-label>
                </q-item-section>
              </q-item>
              <q-item>
                <q-item-section avatar><q-icon color="primary" name="link" /></q-item-section>
                <q-item-section>
                  <q-item-label class="text-code">/conversion</q-item-label>
                  <q-item-label caption>{{ t('about.external_api.alt_short_url') }}</q-item-label>
                </q-item-section>
              </q-item>
              <q-item>
                <q-item-section avatar><q-icon color="primary" name="link" /></q-item-section>
                <q-item-section>
                  <q-item-label class="text-code">/conv</q-item-label>
                  <q-item-label caption>{{ t('about.external_api.shortest_url') }}</q-item-label>
                </q-item-section>
              </q-item>
              <q-item>
                <q-item-section avatar><q-icon color="primary" name="link" /></q-item-section>
                <q-item-section>
                  <q-item-label class="text-code">/back/convert.php</q-item-label>
                  <q-item-label caption>{{ t('about.external_api.direct_access') }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>

            <div class="text-h6 q-mt-lg q-mb-sm">{{ t('about.external_api.features') }}</div>
            <q-banner rounded class="bg-green-1 q-mb-md">
              <template v-slot:avatar>
                <q-icon name="security" color="positive" />
              </template>
              <strong>{{ t('about.external_api.public_access') }}</strong><br>
              {{ t('about.external_api.public_access_desc') }}
            </q-banner>

            <div class="text-h6 q-mt-lg q-mb-sm">{{ t('about.external_api.required_params') }}</div>
            <q-list bordered separator dense>
              <q-item>
                <q-item-section avatar><q-icon color="red" name="star" /></q-item-section>
                <q-item-section>
                  <q-item-label><strong>pixel_id</strong></q-item-label>
                  <q-item-label caption>{{ t('about.external_api.pixel_id_required') }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>

            <div class="text-h6 q-mt-lg q-mb-sm">{{ t('about.external_api.optional_params') }}</div>
            <div class="row q-gutter-md">
              <div class="col-12 col-md-6">
                <q-list bordered separator dense>
                  <q-item-label header>{{ t('about.external_api.basic_data') }}</q-item-label>
                  <q-item>
                    <q-item-section>
                      <q-item-label><strong>event_name</strong></q-item-label>
                      <q-item-label caption>{{ t('about.external_api.event_name_default') }}</q-item-label>
                    </q-item-section>
                  </q-item>
                  <q-item>
                    <q-item-section>
                      <q-item-label><strong>email</strong></q-item-label>
                      <q-item-label caption>{{ t('about.external_api.email_user') }}</q-item-label>
                    </q-item-section>
                  </q-item>
                  <q-item>
                    <q-item-section>
                      <q-item-label><strong>phone</strong></q-item-label>
                      <q-item-label caption>{{ t('about.external_api.phone_with_code') }}</q-item-label>
                    </q-item-section>
                  </q-item>
                  <q-item>
                    <q-item-section>
                      <q-item-label><strong>first_name, last_name</strong></q-item-label>
                      <q-item-label caption>{{ t('about.external_api.first_last_name') }}</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </div>
              <div class="col-12 col-md-6">
                <q-list bordered separator dense>
                  <q-item-label header>{{ t('about.external_api.technical_data') }}</q-item-label>
                  <q-item>
                    <q-item-section>
                      <q-item-label><strong>url</strong></q-item-label>
                      <q-item-label caption>{{ t('about.external_api.page_url') }}</q-item-label>
                    </q-item-section>
                  </q-item>
                  <q-item>
                    <q-item-section>
                      <q-item-label><strong>client_ip_address</strong></q-item-label>
                      <q-item-label caption>{{ t('about.external_api.user_ip') }}</q-item-label>
                    </q-item-section>
                  </q-item>
                  <q-item>
                    <q-item-section>
                      <q-item-label><strong>fbc, fbp</strong></q-item-label>
                      <q-item-label caption>{{ t('about.external_api.facebook_cookies') }}</q-item-label>
                    </q-item-section>
                  </q-item>
                  <q-item>
                    <q-item-section>
                      <q-item-label><strong>event_id</strong></q-item-label>
                      <q-item-label caption>{{ t('about.external_api.unique_id') }}</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </div>
            </div>

            <div class="text-h6 q-mt-lg q-mb-sm">{{ t('about.external_api.supported_events') }}</div>
            <div class="row q-gutter-sm">
              <q-chip v-for="event in supportedEvents" :key="event" color="primary" text-color="white" size="sm">
                {{ event }}
              </q-chip>
            </div>
          </q-card-section>
        </q-card>

        <!-- 6. Примеры интеграции -->
        <q-card id="api-examples" class="q-mb-lg">
          <q-card-section>
            <div class="text-h5 q-mb-md">💻 6. {{ t('about.api_examples.title') }}</div>

            <div class="text-h6 q-mb-sm">{{ t('about.api_examples.simple_gets') }}</div>

            <q-expansion-item icon="star" :label="`1. ${t('about.api_examples.minimal_lead')}`" default-opened>
              <q-card class="q-ma-sm bg-blue-1">
                <q-card-section>
                  <div class="text-subtitle2 q-mb-sm">GET {{ t('common.request') }}:</div>
                  <pre class="text-code">{{ apiEndpoint }}/convert?pixel_id=123456789</pre>
                  <div class="text-caption q-mt-sm">{{ t('about.api_examples.minimal_lead_desc') }}</div>
                </q-card-section>
              </q-card>
            </q-expansion-item>

            <q-expansion-item icon="email" :label="`2. ${t('about.api_examples.lead_with_email')}`">
              <q-card class="q-ma-sm bg-green-1">
                <q-card-section>
                  <div class="text-subtitle2 q-mb-sm">GET {{ t('common.request') }}:</div>
                  <pre class="text-code">{{ apiEndpoint }}/convert?pixel_id=123456789&email=user@example.com&url=https://mysite.com/landing</pre>
                  <div class="text-caption q-mt-sm">{{ t('about.api_examples.lead_with_email_desc') }}</div>
                </q-card-section>
              </q-card>
            </q-expansion-item>

            <q-expansion-item icon="phone" :label="`3. ${t('about.api_examples.full_lead')}`">
              <q-card class="q-ma-sm bg-orange-1">
                <q-card-section>
                  <div class="text-subtitle2 q-mb-sm">GET {{ t('common.request') }}:</div>
                  <pre class="text-code">{{ apiEndpoint }}/convert?pixel_id=123456789&email=user@example.com&phone=+79001234567&first_name=Иван&last_name=Петров&url=https://mysite.com</pre>
                  <div class="text-caption q-mt-sm">{{ t('about.api_examples.full_lead_desc') }}</div>
                </q-card-section>
              </q-card>
            </q-expansion-item>

            <q-expansion-item icon="shopping_cart" :label="`4. ${t('about.api_examples.purchase')}`">
              <q-card class="q-ma-sm bg-purple-1">
                <q-card-section>
                  <div class="text-subtitle2 q-mb-sm">GET {{ t('common.request') }}:</div>
                  <pre class="text-code">{{ apiEndpoint }}/convert?pixel_id=123456789&email=user@example.com&event_name=Purchase&value=99.99&currency=USD</pre>
                  <div class="text-caption q-mt-sm">{{ t('about.api_examples.purchase_desc') }}</div>
                </q-card-section>
              </q-card>
            </q-expansion-item>

            <div class="text-h6 q-mt-lg q-mb-sm">{{ t('about.api_examples.post_examples') }}</div>

            <q-tabs v-model="exampleTab" dense class="text-grey" active-color="primary" indicator-color="primary" align="justify">
              <q-tab name="curl" label="cURL" />
              <q-tab name="php" label="PHP" />
              <q-tab name="javascript" label="JavaScript" />
              <q-tab name="python" label="Python" />
            </q-tabs>

            <q-tab-panels v-model="exampleTab" animated>
              <q-tab-panel name="curl">
                <div class="text-subtitle1 q-mb-sm">POST {{ t('common.request') }} cURL:</div>
                <pre class="api-example bg-grey-9 text-white q-pa-md rounded">{{ curlExample }}</pre>
              </q-tab-panel>

              <q-tab-panel name="php">
                <div class="text-subtitle1 q-mb-sm">PHP {{ t('common.code') }}:</div>
                <pre class="api-example bg-grey-9 text-white q-pa-md rounded">{{ phpExample }}</pre>
              </q-tab-panel>

              <q-tab-panel name="javascript">
                <div class="text-subtitle1 q-mb-sm">JavaScript (fetch API):</div>
                <pre class="api-example bg-grey-9 text-white q-pa-md rounded">{{ jsExample }}</pre>
              </q-tab-panel>

              <q-tab-panel name="python">
                <div class="text-subtitle1 q-mb-sm">Python (requests):</div>
                <pre class="api-example bg-grey-9 text-white q-pa-md rounded">{{ pythonExample }}</pre>
              </q-tab-panel>
            </q-tab-panels>

            <div class="text-h6 q-mt-lg q-mb-sm">{{ t('about.api_examples.post_structure') }}</div>
            <pre class="api-example bg-grey-9 text-white q-pa-md rounded">{{ jsonStructure }}</pre>

            <q-banner rounded class="bg-blue-1 q-mt-md">
              <template v-slot:avatar>
                <q-icon name="lightbulb" color="primary" />
              </template>
              <strong>{{ t('about.api_examples.tip') }}</strong>
            </q-banner>
          </q-card-section>
        </q-card>

        <!-- 7. Устранение проблем -->
        <q-card id="troubleshooting" class="q-mb-lg">
          <q-card-section>
            <div class="text-h5 q-mb-md">🔧 7. {{ t('about.troubleshooting.title') }}</div>

            <q-expansion-item icon="error" :label="t('about.troubleshooting.token_expired')">
              <q-card class="q-ma-sm">
                <q-card-section>
                  <strong>{{ t('about.troubleshooting.token_expired_cause') }}</strong><br>
                  <strong>{{ t('about.troubleshooting.token_expired_solution') }}</strong>
                </q-card-section>
              </q-card>
            </q-expansion-item>

            <q-expansion-item icon="error" :label="t('about.troubleshooting.pixel_not_found')">
              <q-card class="q-ma-sm">
                <q-card-section>
                  <strong>{{ t('about.troubleshooting.pixel_not_found_cause') }}</strong><br>
                  <strong>{{ t('about.troubleshooting.pixel_not_found_solution') }}</strong>
                </q-card-section>
              </q-card>
            </q-expansion-item>

            <q-expansion-item icon="error" :label="t('about.troubleshooting.low_quality')">
              <q-card class="q-ma-sm">
                <q-card-section>
                  <strong>{{ t('about.troubleshooting.low_quality_cause') }}</strong><br>
                  <strong>{{ t('about.troubleshooting.low_quality_solution') }}</strong>
                </q-card-section>
              </q-card>
            </q-expansion-item>

            <q-expansion-item icon="error" :label="t('about.troubleshooting.convert_not_working')">
              <q-card class="q-ma-sm">
                <q-card-section>
                  <strong>{{ t('about.troubleshooting.convert_not_working_cause') }}</strong><br>
                  <strong>{{ t('about.troubleshooting.convert_not_working_solution') }}</strong>
                </q-card-section>
              </q-card>
            </q-expansion-item>

            <q-expansion-item icon="error" :label="t('about.troubleshooting.pixel_id_required')">
              <q-card class="q-ma-sm">
                <q-card-section>
                  <strong>{{ t('about.troubleshooting.pixel_id_required_cause') }}</strong><br>
                  <strong>{{ t('about.troubleshooting.pixel_id_required_solution') }}</strong>
                </q-card-section>
              </q-card>
            </q-expansion-item>

            <q-expansion-item icon="error" :label="t('about.troubleshooting.events_not_visible')">
              <q-card class="q-ma-sm">
                <q-card-section>
                  <strong>{{ t('about.troubleshooting.events_not_visible_cause') }}</strong><br>
                  <strong>{{ t('about.troubleshooting.events_not_visible_solution') }}</strong>
                </q-card-section>
              </q-card>
            </q-expansion-item>
          </q-card-section>
        </q-card>

        <!-- 8. Лучшие практики -->
        <q-card id="best-practices" class="q-mb-lg">
          <q-card-section>
            <div class="text-h5 q-mb-md">⭐ 8. {{ t('about.best_practices.title') }}</div>

            <div class="row q-gutter-md">
              <div class="col-12 col-md-6">
                <q-card flat bordered class="bg-green-1 full-height">
                  <q-card-section>
                    <div class="text-h6 text-positive q-mb-sm">✅ {{ t('about.best_practices.recommended') }}</div>
                    <q-list dense>
                      <q-item>
                        <q-item-section avatar><q-icon size="sm" name="check" color="positive" /></q-item-section>
                        <q-item-section>{{ t('about.best_practices.use_unique_ids') }}</q-item-section>
                      </q-item>
                      <q-item>
                        <q-item-section avatar><q-icon size="sm" name="check" color="positive" /></q-item-section>
                        <q-item-section>{{ t('about.best_practices.pass_max_data') }}</q-item-section>
                      </q-item>
                      <q-item>
                        <q-item-section avatar><q-icon size="sm" name="check" color="positive" /></q-item-section>
                        <q-item-section>{{ t('about.best_practices.send_realtime') }}</q-item-section>
                      </q-item>
                      <q-item>
                        <q-item-section avatar><q-icon size="sm" name="check" color="positive" /></q-item-section>
                        <q-item-section>{{ t('about.best_practices.use_short_urls') }}</q-item-section>
                      </q-item>
                      <q-item>
                        <q-item-section avatar><q-icon size="sm" name="check" color="positive" /></q-item-section>
                        <q-item-section>{{ t('about.best_practices.test_minimal') }}</q-item-section>
                      </q-item>
                      <q-item>
                        <q-item-section avatar><q-icon size="sm" name="check" color="positive" /></q-item-section>
                        <q-item-section>{{ t('about.best_practices.check_events_manager') }}</q-item-section>
                      </q-item>
                    </q-list>
                  </q-card-section>
                </q-card>
              </div>
              <div class="col-12 col-md-6">
                <q-card flat bordered class="bg-red-1 full-height">
                  <q-card-section>
                    <div class="text-h6 text-negative q-mb-sm">❌ {{ t('about.best_practices.not_recommended') }}</div>
                    <q-list dense>
                      <q-item>
                        <q-item-section avatar><q-icon size="sm" name="close" color="negative" /></q-item-section>
                        <q-item-section>{{ t('about.best_practices.dont_forget_pixel') }}</q-item-section>
                      </q-item>
                      <q-item>
                        <q-item-section avatar><q-icon size="sm" name="close" color="negative" /></q-item-section>
                        <q-item-section>{{ t('about.best_practices.no_duplicate_events') }}</q-item-section>
                      </q-item>
                      <q-item>
                        <q-item-section avatar><q-icon size="sm" name="close" color="negative" /></q-item-section>
                        <q-item-section>{{ t('about.best_practices.no_pixel_only') }}</q-item-section>
                      </q-item>
                      <q-item>
                        <q-item-section avatar><q-icon size="sm" name="close" color="negative" /></q-item-section>
                        <q-item-section>{{ t('about.best_practices.no_same_event_id') }}</q-item-section>
                      </q-item>
                      <q-item>
                        <q-item-section avatar><q-icon size="sm" name="close" color="negative" /></q-item-section>
                        <q-item-section>{{ t('about.best_practices.no_ignore_quality') }}</q-item-section>
                      </q-item>
                      <q-item>
                        <q-item-section avatar><q-icon size="sm" name="close" color="negative" /></q-item-section>
                        <q-item-section>{{ t('about.best_practices.no_delayed_events') }}</q-item-section>
                      </q-item>
                    </q-list>
                  </q-card-section>
                </q-card>
              </div>
            </div>

            <div class="text-h6 q-mt-lg q-mb-sm">{{ t('about.best_practices.integration_systems') }}</div>
            <q-card flat bordered class="bg-blue-1">
              <q-card-section>
                <div class="row q-gutter-md">
                  <div class="col-12 col-md-4">
                    <div class="text-center">
                      <q-icon name="web" size="lg" color="primary" />
                      <div class="text-subtitle2 q-mt-sm">{{ t('about.best_practices.web_forms') }}</div>
                      <div class="text-caption">{{ t('about.best_practices.web_forms_quality') }}</div>
                      <div class="text-body2 q-mt-sm">{{ t('about.best_practices.web_forms_data') }}</div>
                    </div>
                  </div>
                  <div class="col-12 col-md-4">
                    <div class="text-center">
                      <q-icon name="smartphone" size="lg" color="primary" />
                      <div class="text-subtitle2 q-mt-sm">{{ t('about.best_practices.mobile_apps') }}</div>
                      <div class="text-caption">{{ t('about.best_practices.mobile_apps_quality') }}</div>
                      <div class="text-body2 q-mt-sm">{{ t('about.best_practices.mobile_apps_data') }}</div>
                    </div>
                  </div>
                  <div class="col-12 col-md-4">
                    <div class="text-center">
                      <q-icon name="business" size="lg" color="primary" />
                      <div class="text-subtitle2 q-mt-sm">{{ t('about.best_practices.crm_systems') }}</div>
                      <div class="text-caption">{{ t('about.best_practices.crm_systems_quality') }}</div>
                      <div class="text-body2 q-mt-sm">{{ t('about.best_practices.crm_systems_data') }}</div>
                    </div>
                  </div>
                </div>
              </q-card-section>
            </q-card>

            <div class="text-h6 q-mt-lg q-mb-sm">{{ t('about.best_practices.quick_start') }}</div>
            <q-timeline color="primary">
              <q-timeline-entry icon="web" :title="`1. ${t('about.best_practices.find_pixel_id')}`">
                <div>{{ t('about.best_practices.find_pixel_id_desc') }}</div>
              </q-timeline-entry>
              <q-timeline-entry icon="send" :title="`2. ${t('about.best_practices.send_test_event')}`">
                <div>
                  {{ t('about.best_practices.send_test_event_desc') }}<br>
                  <code class="text-code">{{ apiEndpoint }}/convert?pixel_id=ВАШ_PIXEL_ID&email=test@example.com</code>
                </div>
              </q-timeline-entry>
              <q-timeline-entry icon="visibility" :title="`3. ${t('about.best_practices.check_facebook')}`">
                <div>{{ t('about.best_practices.check_facebook_desc') }}</div>
              </q-timeline-entry>
              <q-timeline-entry icon="integration_instructions" :title="`4. ${t('about.best_practices.integrate_project')}`">
                <div>{{ t('about.best_practices.integrate_project_desc') }}</div>
              </q-timeline-entry>
            </q-timeline>
          </q-card-section>
        </q-card>

        <!-- Контакты и поддержка -->
        <q-card class="q-mb-lg">
          <q-card-section class="bg-grey-2">
            <div class="text-h6 q-mb-md">📞 {{ t('about.support.title') }}</div>
            <div class="row q-gutter-md">
              <div class="col-12 col-md-6">
                <q-list>
                  <q-item>
                    <q-item-section avatar>
                      <q-icon name="description" color="primary" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>{{ t('about.support.facebook_docs') }}</q-item-label>
                      <q-item-label caption>
                        <a href="https://developers.facebook.com/docs/marketing-api/conversions-api" target="_blank" class="text-primary">
                          Facebook Conversions API Docs
                        </a>
                      </q-item-label>
                    </q-item-section>
                  </q-item>
                  <q-item>
                    <q-item-section avatar>
                      <q-icon name="bug_report" color="primary" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>{{ t('about.support.system_logs') }}</q-item-label>
                      <q-item-label caption>{{ t('about.support.system_logs_desc') }}</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </div>
              <div class="col-12 col-md-6">
                <q-list>
                  <q-item>
                    <q-item-section avatar>
                      <q-icon name="analytics" color="primary" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>{{ t('about.support.events_manager') }}</q-item-label>
                      <q-item-label caption>
                        <a href="https://business.facebook.com/events_manager2" target="_blank" class="text-primary">
                          {{ t('about.support.events_manager_desc') }}
                        </a>
                      </q-item-label>
                    </q-item-section>
                  </q-item>
                  <q-item>
                    <q-item-section avatar>
                      <q-icon name="help" color="primary" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>{{ t('about.support.test_events') }}</q-item-label>
                      <q-item-label caption>{{ t('about.support.test_events_desc') }}</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </div>
            </div>
          </q-card-section>
        </q-card>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const step = ref(1)
const exampleTab = ref('curl')
const leadExamples = computed(() => JSON.parse(t('about.sending_conversions.lead_examples')))
const purchaseExamples = computed(() => JSON.parse(t('about.sending_conversions.purchase_examples')))
const apiEndpoint = computed(() => {
  return window.location.origin
})

const supportedEvents = [
  'Lead', 'Purchase', 'CompleteRegistration', 'AddToCart',
  'ViewContent', 'Contact', 'SubmitApplication', 'InitiateCheckout',
  'Subscribe', 'StartTrial', 'Search', 'AddToWishlist'
]

const curlExample = `curl -X POST "${window.location.origin}/convert" \\
  -H "Content-Type: application/json" \\
  -d '{
    "pixel_id": "123456789",
    "event_name": "Lead",
    "email": "user@example.com",
    "phone": "+79001234567",
    "first_name": "Иван",
    "last_name": "Петров",
    "url": "https://mysite.com/contact"
  }'

# Или через GET:
curl "${window.location.origin}/convert?pixel_id=123456789&email=user@example.com&event_name=Lead"`

const phpExample = `<?php
// Способ 1: POST запрос
$data = [
    'pixel_id' => '123456789',
    'event_name' => 'Lead',
    'email' => 'user@example.com',
    'phone' => '+79001234567',
    'first_name' => 'Иван',
    'last_name' => 'Петров',
    'url' => 'https://mysite.com/contact'
];

$ch = curl_init('${window.location.origin}/convert');
curl_setopt($ch, CURLOPT_POST, 1);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
curl_setopt($ch, CURLOPT_HTTPHEADER, ['Content-Type: application/json']);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
curl_close($ch);

// Способ 2: GET запрос (проще)
$url = '${window.location.origin}/convert?' . http_build_query([
    'pixel_id' => '123456789',
    'email' => 'user@example.com',
    'event_name' => 'Lead'
]);
$response = file_get_contents($url);
echo $response;
?>`

const jsExample = `// Способ 1: POST запрос
const data = {
  pixel_id: '123456789',
  event_name: 'Lead',
  email: 'user@example.com',
  phone: '+79001234567',
  first_name: 'Иван',
  last_name: 'Петров',
  url: 'https://mysite.com/contact'
};

fetch('${window.location.origin}/convert', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json'
  },
  body: JSON.stringify(data)
})
.then(response => response.json());

// Способ 2: GET запрос (проще)
const params = new URLSearchParams({
  pixel_id: '123456789',
  email: 'user@example.com',
  event_name: 'Lead'
});

fetch('${window.location.origin}/convert?' + params)
.then(response => response.json());`

const pythonExample = `import requests
import json

# Способ 1: POST запрос
data = {
    'pixel_id': '123456789',
    'event_name': 'Lead',
    'email': 'user@example.com',
    'phone': '+79001234567',
    'first_name': 'Иван',
    'last_name': 'Петров',
    'url': 'https://mysite.com/contact'
}

response = requests.post(
    '${window.location.origin}/convert',
    headers={'Content-Type': 'application/json'},
    data=json.dumps(data)
)

# Способ 2: GET запрос (проще)
params = {
    'pixel_id': '123456789',
    'email': 'user@example.com',
    'event_name': 'Lead'
}

response = requests.get('${window.location.origin}/convert', params=params)
print(response.json())`

const jsonStructure = `// POST данные для /convert
{
  "pixel_id": "123456789",           // ID Facebook пикселя (обязательно)
  "event_name": "Lead",              // Тип события (по умолчанию: Lead)

  // Контактные данные (чем больше, тем лучше качество)
  "email": "user@example.com",       // Email пользователя
  "phone": "+79001234567",           // Телефон с кодом страны
  "first_name": "Иван",              // Имя
  "last_name": "Петров",             // Фамилия

  // Дополнительные данные
  "url": "https://mysite.com/page",  // URL страницы события
  "event_id": "unique_123",          // Уникальный ID (автогенерация)

  // Для Purchase событий
  "value": 99.99,                    // Стоимость
  "currency": "USD",                 // Валюта

  // Facebook куки (если доступны)
  "fbc": "fb.1.xxx...",              // Facebook Click ID
  "fbp": "fb.1.xxx...",              // Facebook Browser ID

  // Технические данные (автоопределение)
  "client_ip_address": "192.168.1.1", // IP адрес
  "client_user_agent": "Mozilla..."    // User Agent
}`

const scrollTo = (elementId) => {
  const element = document.getElementById(elementId)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}
</script>

<style scoped>
.api-example {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.4;
  overflow-x: auto;
}

.text-code {
  font-family: 'Courier New', monospace;
  background-color: #f5f5f5;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
}

pre {
  white-space: pre-wrap;
  word-wrap: break-word;
}

.q-stepper {
  box-shadow: none;
}
</style>